import React, { useState, useEffect } from 'react';
import { usePublishedProducts } from '../hooks/usePublishedProducts';
import { useCategories } from '../hooks/useCategories';
import { useNavigate } from 'react-router-dom';
import { IoSearch, IoGrid, IoList, IoChevronBack, IoChevronForward } from 'react-icons/io5';
import ChatButton from '../components/ChatButton';

export const Products = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('All');
  const [selectedBrand, setSelectedBrand] = useState<string>('All');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'category' | 'price-low' | 'price-high' | 'newest'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  
  const { categories, loading: categoriesLoading } = useCategories();
  const { products, loading, error } = usePublishedProducts(
    selectedCategoryId === 'All' ? undefined : selectedCategoryId
  );

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategoryId, selectedBrand, sortBy]);

  // Get unique brands from products
  const uniqueBrands = Array.from(new Set(products.map(p => p.brand).filter(Boolean))).sort();

  // Filter products by search query, brand, and only show published products
  const filteredProducts = products.filter(product => {
    // Only show published products
    if (!product.isPublished) return false;
    
    const name = product.name || '';
    const description = product.description || '';
    const brand = product.brand || '';
    const query = searchQuery.toLowerCase();
    
    const matchesSearch = (
      name.toLowerCase().includes(query) ||
      description.toLowerCase().includes(query) ||
      brand.toLowerCase().includes(query)
    );

    const matchesBrand = selectedBrand === 'All' || product.brand === selectedBrand;
    
    return matchesSearch && matchesBrand;
  });

  // Sort products based on selected sort option
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'category':
        return (a.categoryName || '').localeCompare(b.categoryName || '');
      case 'price-low':
        return (a.price || 0) - (b.price || 0);
      case 'price-high':
        return (b.price || 0) - (a.price || 0);
      case 'newest':
      default:
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
    }
  });

  // Pagination
  const itemsPerPage = isMobile ? 12 : 36;
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, endIndex);

  // Scroll to top when page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = isMobile ? 3 : 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if near the start
      if (currentPage <= 2) {
        endPage = Math.min(maxVisiblePages - 1, totalPages - 1);
      }
      
      // Adjust if near the end
      if (currentPage >= totalPages - 1) {
        startPage = Math.max(2, totalPages - (maxVisiblePages - 2));
      }
      
      // Add ellipsis if needed
      if (startPage > 2) {
        pages.push('...');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  if (loading || categoriesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-amber-600 mb-4"></div>
          <p className="text-gray-700 font-medium text-lg">Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-slate-50">
        <div className="text-center">
          <p className="text-red-600 font-medium text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-12">
          <div className="text-amber-600 text-sm font-bold uppercase tracking-widest mb-2">Shop</div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Browse Our Products</h1>
          <p className="text-lg text-gray-700">Discover our wide selection of computer components and peripherals from industry-leading brands</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 mb-10">
          {/* Top Row: Search and Controls */}
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between mb-6">
            {/* Search Bar */}
            <div className="relative flex-1 w-full lg:max-w-lg">
              <IoSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by product name, brand, or category..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all font-medium text-gray-900"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <label className="text-sm font-semibold text-gray-700">Sort:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white font-medium text-gray-900 transition-all"
              >
                <option value="newest">Newest First</option>
                <option value="name">Name (A-Z)</option>
                <option value="category">Category</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-2 border border-gray-300 rounded-lg p-1 bg-gray-50">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-3 rounded-md transition-all font-medium ${
                  viewMode === 'grid'
                    ? 'bg-amber-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Grid view"
              >
                <IoGrid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-3 rounded-md transition-all font-medium ${
                  viewMode === 'list'
                    ? 'bg-amber-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="List view"
              >
                <IoList size={18} />
              </button>
            </div>
          </div>

          {/* Category Filter */}
          <div className="mb-6">
            <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Categories</h4>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategoryId('All')}
                className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  selectedCategoryId === 'All'
                    ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-md hover:shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                All Products
              </button>
              {categories.length > 0 ? (
                categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategoryId(category.id)}
                    className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
                      selectedCategoryId === category.id
                        ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-md hover:shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                    }`}
                  >
                    {category.name}
                  </button>
                ))
              ) : (
                <span className="text-sm text-gray-500 italic py-2">
                  No categories available
                </span>
              )}
            </div>
          </div>

          {/* Brand Filter */}
          {uniqueBrands.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Brands</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedBrand('All')}
                  className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
                    selectedBrand === 'All'
                      ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-md hover:shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  All Brands
                </button>
                {uniqueBrands.map((brand) => (
                  <button
                    key={brand}
                    onClick={() => setSelectedBrand(brand)}
                    className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
                      selectedBrand === brand
                        ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-md hover:shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                    }`}
                  >
                    {brand}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results Count and Filters Info */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div className="text-gray-700 font-medium">
            <span className="text-xl font-bold text-gray-900">{sortedProducts.length}</span>
            <span className="ml-2">{sortedProducts.length === 1 ? 'product' : 'products'} found</span>
            {searchQuery && <span className="text-amber-600 ml-2">• Search active</span>}
            {selectedCategoryId !== 'All' && <span className="text-amber-600 ml-2">• {categories.find(c => c.id === selectedCategoryId)?.name}</span>}
            {selectedBrand !== 'All' && <span className="text-amber-600 ml-2">• {selectedBrand}</span>}
          </div>
          {(searchQuery || selectedCategoryId !== 'All' || selectedBrand !== 'All') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategoryId('All');
                setSelectedBrand('All');
              }}
              className="text-sm text-amber-600 hover:text-amber-700 font-semibold underline"
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Products Grid/List */}
        {sortedProducts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-16 text-center">
            <div className="text-5xl mb-4">🔍</div>
            <p className="text-gray-700 text-lg font-medium mb-2">No products found</p>
            <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategoryId('All');
                setSelectedBrand('All');
              }}
              className="px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <>
            <div
              className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                  : 'space-y-4'
              }
            >
              {paginatedProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => navigate(`/products/${product.id}`)}
                className={`bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer group ${
                  viewMode === 'list' ? 'flex hover:-translate-y-1' : 'hover:-translate-y-2'
                }`}
              >
                {/* Product Image Placeholder */}
                <div
                  className={`bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center group-hover:from-amber-100 group-hover:to-yellow-100 transition-all duration-300 overflow-hidden relative ${
                    viewMode === 'grid' ? 'h-56' : 'w-56 h-56 flex-shrink-0'
                  }`}
                >
                  {/* Stock Badge */}
                  {(() => {
                    const totalStock = product.hasVariants && product.variants && product.variants.length > 0
                      ? product.variants.reduce((sum, variant) => sum + (parseInt(variant.baseStock || '0') || 0), 0)
                      : parseInt(product.baseStock || '0') || 0;
                    
                    if (product.stockType === 'order-basis') {
                      return <div className="absolute top-3 right-3 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold">Available</div>;
                    } else if (totalStock >= 2) {
                      return <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">In Stock</div>;
                    } else if (totalStock > 0) {
                      return <div className="absolute top-3 right-3 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold">Low Stock</div>;
                    } else {
                      return <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">Out of Stock</div>;
                    }
                  })()}
                  
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="text-7xl text-gray-400 group-hover:text-amber-500 transition-colors duration-200 group-hover:scale-125">
                      {product.categoryName === 'CPU' && '🖥️'}
                      {product.categoryName === 'GPU' && '🎮'}
                      {product.categoryName === 'RAM' && '💾'}
                      {product.categoryName === 'Storage' && '💿'}
                      {product.categoryName === 'Motherboard' && '⚡'}
                      {product.categoryName === 'Case' && '📦'}
                      {product.categoryName === 'Power Supply' && '🔌'}
                      {product.categoryName === 'Cooling' && '❄️'}
                      {product.categoryName === 'Peripherals' && '⌨️'}
                      {product.categoryName === 'Monitor' && '🖥️'}
                      {product.categoryName === 'Pre-built PC' && '💻'}
                      {!['CPU', 'GPU', 'RAM', 'Storage', 'Motherboard', 'Case', 'Power Supply', 'Cooling', 'Peripherals', 'Monitor', 'Pre-built PC'].includes(product.categoryName || '') && '🔧'}
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className={`flex-1 flex flex-col ${ viewMode === 'grid' ? 'p-6' : 'p-8'}`}>
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1.5 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-900 text-xs font-bold rounded-full border border-amber-200">
                      {product.categoryName || 'Product'}
                    </span>
                    {product.hasVariants && product.variants && product.variants.length > 0 && (
                      <span className="inline-block ml-2 px-3 py-1.5 bg-purple-100 text-purple-900 text-xs font-bold rounded-full border border-purple-200">
                        {product.variants.length} Options
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-amber-600 transition-colors mb-2 line-clamp-2">
                    {product.name}
                  </h3>
                  
                  <p className="text-sm font-semibold text-amber-700 mb-3">{product.brand}</p>
                  
                  <p className={`text-gray-700 mb-5 flex-grow ${ viewMode === 'grid' ? 'line-clamp-2 text-sm' : 'line-clamp-2 text-base'}`}>
                    {product.description}
                  </p>

                  {/* Key Specs (Grid view only) */}
                  {viewMode === 'grid' && Object.keys(product.specifications).length > 0 && (
                    <div className="mb-5 pb-5 border-b border-gray-200">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {Object.entries(product.specifications)
                          .slice(0, 4)
                          .map(([key, value]) => (
                            <div key={key} className="text-gray-700">
                              <span className="font-semibold text-gray-900">{key}:</span>
                              <span className="ml-1">{String(value).substring(0, 15)}...</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* View Details CTA */}
                  <div className="text-center">
                    <button className="w-full px-4 py-3 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg font-bold hover:shadow-lg transition-all group-hover:from-amber-700 group-hover:to-amber-800">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Page Info */}
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>

                {/* Pagination Buttons */}
                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-2 px-5 py-3 rounded-lg font-bold transition-all ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-amber-600 to-amber-700 border border-amber-700 text-white hover:shadow-lg'
                    }`}
                  >
                    <IoChevronBack size={16} />
                    <span className="hidden sm:inline">Previous</span>
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((page, index) => (
                      <React.Fragment key={index}>
                        {page === '...' ? (
                          <span className="px-3 py-2 text-gray-400">...</span>
                        ) : (
                          <button
                            onClick={() => handlePageChange(page as number)}
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${
                              currentPage === page
                                ? 'bg-zinc-800 text-white shadow-md'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                            }`}
                          >
                            {page}
                          </button>
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`flex items-center gap-2 px-5 py-3 rounded-lg font-bold transition-all ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-amber-600 to-amber-700 border border-amber-700 text-white hover:shadow-lg'
                    }`}
                  >
                    <span className="hidden sm:inline">Next</span>
                    <IoChevronForward size={16} />
                  </button>
                </div>

                {/* Jump to Page (Desktop only) */}
                {!isMobile && totalPages > 5 && (
                  <div className="flex items-center gap-2">
                    <label htmlFor="pageJump" className="text-sm text-gray-600">
                      Go to:
                    </label>
                    <select
                      id="pageJump"
                      value={currentPage}
                      onChange={(e) => handlePageChange(Number(e.target.value))}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white text-sm"
                    >
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <option key={page} value={page}>
                          Page {page}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <ChatButton />
    </div>
  );
};
